import {BehaviorSubject, Observable, of as observableOf, share, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {Store, select} from '@ngrx/store';

import {
    DirectoryUser,
    INVALID_PASSWORD_ERROR_MESSAGE,
    INVALID_PASSWORD_STATUS_CODE,
    NUMBER_OF_DAYS_TO_SHOW_NOTIFICATION_BEFORE_PASSWORD_EXPIRES,
    MfaConfigs,
    User,
    UserMfaConfig,
    UserStatus,
    MFA_ORDER,
    SEND_SMS_FAILED_STATUS_CODE
} from '../model/user';
import {environment} from '@env/environment';
import {getAuthenticatedUser, State} from '@app/app.reducers';
import {AuthenticatedUserRefreshAction} from '@app/views/common/auth.actions';
import {IntegrationState} from '@app/views/admin/application-settings/models/integration-state.model';
import {Application} from '@app/views/admin/application-settings/models/application.model';
import {PASSPORT_LOGIN_IDP_STATUS} from '@app/views/sso/identity-providers/idp-settings/passport-login-idp/passport-login-idp.model';
import {UntypedFormControl} from '@angular/forms';
import {UKR_PHONE_NUMBERS_WITH_COUNTRY_CODE_LENGTH, US_PHONE_NUMBERS_WITHOUT_COUNTRY_CODE_LENGTH} from '@app/core/utils/other-utils';
import {customer} from '@env/customer';


@Injectable()
export class AuthenticatedUserService {

    authenticatedUser$: Observable<User>;
    private authenticatedUser_: BehaviorSubject<User>;
    authenticatedUser: User;
    previousUserRole: string;
    passwordExpirationPolicy: number;
    private _passwordExpirationPolicy: number;
    passwordExpirationPolicy$: Observable<number> = this.getPasswordExpirationPolicyData().pipe(share());
    private _isMainNotificationClosed$: BehaviorSubject<boolean> = new BehaviorSubject(null);
    isMainNotificationClosed$: Observable<boolean> = this._isMainNotificationClosed$.asObservable();

    permissionsMap: Map<string, PermissionData> = new Map();
    roleOrgTypesMap: Map<string, string[]> = new Map([
        ['APPLICATION_ADMIN', ['app_vendor']],
        ['INSTITUTIONAL_ADMIN', ['county', 'district', 'independent_school']],
        ['SYS_ADMIN', ['root']]
    ]);


    static transformPrePopulatedMFAPhoneNumber(predefinedPhone: string, orgCountry: string): string {
        let prePopulatedMFAPhoneNumber: string = predefinedPhone;

        if ((orgCountry === 'US')
            && (prePopulatedMFAPhoneNumber?.length === US_PHONE_NUMBERS_WITHOUT_COUNTRY_CODE_LENGTH)) {
            prePopulatedMFAPhoneNumber = `+1${prePopulatedMFAPhoneNumber}`;
        } else {

            const isUkrainianNumber = `${prePopulatedMFAPhoneNumber[0]}${prePopulatedMFAPhoneNumber[1]}` === '38';
            if (isUkrainianNumber
                && (prePopulatedMFAPhoneNumber?.length === UKR_PHONE_NUMBERS_WITH_COUNTRY_CODE_LENGTH)) {
                prePopulatedMFAPhoneNumber = `+${prePopulatedMFAPhoneNumber}`;
            }
        }
        return prePopulatedMFAPhoneNumber;
    }

    static isValidPhoneNumberValidator(control: UntypedFormControl): { [s: string]: boolean } {
        const value: string = control?.value ? control.value.trim() : '';

        // reg exp to check if number is like +(1)-213-5-5 (first character '+' - optional, country code in '()' - optional, '-' - optional)
        const resultMatch = value.toString().match(new RegExp('^[\\+]?[(]?[0-9-]{0,}[)]?[-\\s\\.]?[0-9[-]{0,}]{0,}[0-9]{1}?$'));
        // reg exp to find and return typed digits, all other staff will be omitted
        const digitsOnly = (value.toString().match(new RegExp(/\d*/g)) || []).filter((val: string) => !!val).join('');

        if (!value
            || !resultMatch
            || (digitsOnly.length < 3)) {
            return {'phoneNumberIsInvalid': true};
        }
        return null;
    }

    constructor(private http: HttpClient, private store: Store<State>) {
        this.authenticatedUser_ = new BehaviorSubject<User>(new User());
        this.authenticatedUser$ = this.authenticatedUser_.asObservable();

        this.store.pipe(select(getAuthenticatedUser))
            // .filter(u => !!u)
            .subscribe(user => {
                this.authenticatedUser = user;
                this.authenticatedUser_.next(user);
                this.permissionsMap.forEach(pb => {
                    pb.result.next(this.doCheckAuthorization(this.authenticatedUser, pb.permittedRoles, pb.permittedOrgTypes));
                });
            });
    }

    setPreviousUserRole(): string | null {
        this.previousUserRole = (this.authenticatedUser ? this.authenticatedUser.role : null);
        return this.previousUserRole;
    }

    setPasswordExpirationPolicy(passwordExpirationPolicy: number) {
        this._passwordExpirationPolicy = passwordExpirationPolicy;
    }

    getPasswordExpirationPolicy() {
        return this._passwordExpirationPolicy;
    }

    changeIsMainNotificationClosed(isMainNotificationClosed: boolean) {
        this._isMainNotificationClosed$.next(isMainNotificationClosed);
    }

    isVendor(user?: User, strictMatch: boolean = false, checkOrgType = true): boolean {
        const permittedRoles = strictMatch ? ['APPLICATION_ADMIN'] : ['APPLICATION_ADMIN', 'SERVICE_OPERATOR'];
        const permittedOrgTypes = checkOrgType ? ['app_vendor'] : null;
        return this.doCheckAuthorization(user || this.authenticatedUser, permittedRoles, permittedOrgTypes);
    }

    isProvider(user?: User, strictMatch: boolean = false, checkOrgType = true): boolean {
        const permittedRoles = strictMatch ? ['INSTITUTIONAL_ADMIN'] : ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR'];
        const permittedOrgTypes = checkOrgType ? ['county', 'district', 'independent_school'] : null;
        return this.doCheckAuthorization(user || this.authenticatedUser, permittedRoles, permittedOrgTypes);
    }

    isDistrictProvider(user?: User, strictMatch: boolean = false): boolean {
        const permittedRoles = strictMatch ? ['INSTITUTIONAL_ADMIN'] : ['INSTITUTIONAL_ADMIN', 'SERVICE_OPERATOR'];
        return this.doCheckAuthorization(user || this.authenticatedUser, permittedRoles, ['district']);
    }

    isServiceOperator(user?: User): boolean {
        return this.doCheckAuthorization(user || this.authenticatedUser, ['SERVICE_OPERATOR']);
    }

    isSysAdmin(user?: User): boolean {
        return this.doCheckAuthorization(user || this.authenticatedUser, ['SYS_ADMIN']);
    }

    isAuthenticated(user?: User): boolean {
        return !!user || !!this.authenticatedUser;
    }

    isRestrictedInternalLogin(userRole: string = this.authenticatedUser.role): boolean {
        return (environment.customer.restrictedInternalLoginRoles
            && (environment.customer.restrictedInternalLoginRoles.indexOf(userRole) > -1));
    }

    hasRole(permittedRoles: string[], permittedOrgTypes: string[] = null): Observable<boolean> {
        if (permittedRoles) {
            const key: string = permittedRoles.join('') + (permittedOrgTypes ? permittedOrgTypes.join('') : '');
            if (!this.permissionsMap.has(key)) {
                this.permissionsMap.set(key, {
                    permittedRoles: permittedRoles,
                    permittedOrgTypes: permittedOrgTypes,
                    result: new BehaviorSubject<boolean>(
                        this.doCheckAuthorization(this.authenticatedUser, permittedRoles, permittedOrgTypes))
                });
            }
            return this.permissionsMap.get(key).result.asObservable();
        } else {
            return observableOf(false);
        }
    }

    getAuthenticatedUser(): Observable<User> {
        return this.http.get<User>(`${environment.datahubBaseURI}/api/v2/directory/users/me`)
            .pipe(
                map(u => new User(Object.assign({}, u))),
                catchError(this.handleError)
            );
    }

    refreshAuthenticatedUser(): void {
        this.store.dispatch(new AuthenticatedUserRefreshAction());
    }

    doCheckAuthorization(user: User, permittedRoles: string[], permittedOrgTypes: string[] = null): boolean {
        if (user && permittedRoles?.length) {
            if (permittedOrgTypes && permittedOrgTypes.indexOf(user?.orgType) === -1) {
                return false;
            }

            const userRoles = [user?.role];
            // SYS_ADMIN, SERVICE_OPERATOR, APPLICATION_ADMIN, INSTITUTIONAL_ADMIN

            if (userRoles && userRoles.length > 0) {
                return permittedRoles.some(permittedRole => userRoles.indexOf(permittedRole) > -1);
            }

            return false;
        }
        return false;
    }

    getDirectoryUser(guid: string): Observable<any> {
        return this.http
            .get<DirectoryUser>(`${environment.datahubBaseURI}/api/v2/directory/users/${guid}`)
            .pipe(catchError(this.handleError));
    }

    updateDirectoryUser(user: DirectoryUser, guid: string): Observable<DirectoryUser> {
        return this.http
            .put<DirectoryUser>(`${environment.datahubBaseURI}/api/v2/directory/users/${guid}`, user)
            .pipe(
                catchError((error): Observable<any> => {
                    if (error.status === INVALID_PASSWORD_STATUS_CODE) {
                        error.message = INVALID_PASSWORD_ERROR_MESSAGE;
                        return throwError(error);
                    }
                    return this.handleError(error);
                })
            );
    }

    updateCurrentUserPassword(currentPassword: string, newPassword: string) {
        const options = {responseType: 'text' as const};
        const data = {
            currentPassword: currentPassword,
            newPassword: newPassword
        };
        return this.http
            .put(`${environment.datahubBaseURI}/api/v2/directory/users/update/password`, data, options)
            .pipe(
                catchError((error): Observable<any> => {
                    if (error.status === INVALID_PASSWORD_STATUS_CODE) {
                        error.message = INVALID_PASSWORD_ERROR_MESSAGE;
                        return throwError(error);
                    }
                    return this.handleError(error);
                })
            );
    }

    disableUserMFA(user: User): Observable<User | string> {
        return this.http
            .post(`${environment.datahubBaseURI}/mfa/user/disable`, user, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    sendUserVerificationCode(MFAPhone: string): Observable<any> {
        const params = new HttpParams().append('phone', MFAPhone);
        const options = {
            params,
            responseType: 'text' as const
        };
        return this.http
            .get(`${environment.datahubBaseURI}/mfa/send/phone_code`, options)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === SEND_SMS_FAILED_STATUS_CODE) {
                        return throwError(error);
                    } else {
                        return this.handleError(error);
                    }
                })
            );

    }

    verifyCodeForUserMFAPhoneChange(verificationCode: string, user: User): Observable<string[] | string> {
        return this.http
            .post<string[] | string>(`${environment.datahubBaseURI}/mfa/user/enable/${verificationCode}`, user)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === SEND_SMS_FAILED_STATUS_CODE) {
                        return throwError(error);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    regenerateUserMFARecoveryCodes(userGuid: string, password: string): Observable<string[]> {
        return this.http
            .put<string[]>(`${environment.datahubBaseURI}/mfa/users/${userGuid}/reset-codes?action=regenerate`, password)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 403) {
                        return throwError(error);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    downloadRecoveryCodes(userGuid: string, resetCode: string) {
        const options = {
            observe: 'response' as const,
            responseType: 'text' as const
        };
        const formData = new FormData();
        formData.append('userGuid', userGuid);
        formData.append('resetCode', resetCode);
        return this.http.post(`${environment.datahubBaseURI}/mfa/reset_codes/download`, formData, options).pipe(
            map((res: any) => new Blob([res.body], {type: 'text/plain'}))
        );
    }

    getOneTimeLogin(userGuid: string) {
        const options = {
            responseType: 'text' as const
        };
        return this.http.get(`${environment.etxBaseURI}/account/onetimelogin/get/${userGuid}`, options)
            .pipe(catchError(this.handleError));
    }

    resetFaceIdForStudent(userGuid: string): Observable<string> {
        const url = `${environment.etxBaseURI}/account/ext/faceid/api/reset`;
        const params = {
            userGuid: userGuid
        };
        return this.http.post(url, null, {params: params})
            .pipe(
                map((data: string[]) =>
                    data?.length > 0 ? data[0] : ''
                ),
                catchError(this.handleError)
            );
    }

    resetFaceIdForStudentOnPrivateIdServer(puid: string): Observable<number> {
        return this.http.post<{message: string; status: number}>(
            `${environment.privateIdApiUrl}/plainDelete`,
            {api_key: environment.privateIdApiKey, puid: puid}
        ).pipe(
            map((response: {message: string; status: number}) => response.status),
            catchError(this.handleError)
        );
    }

    checkIsFaceIdActiveForUser(userGuid: string): Observable<PASSPORT_LOGIN_IDP_STATUS> {
        const url = `${environment.etxBaseURI}/account/ext/faceid/api/status`;
        const options = {
            params: {
                userGuid: userGuid
            },
            observe: 'response' as const,
            responseType: 'text' as const
        };
        return this.http.get(url, options)
            .pipe(
                map((result: HttpResponse<string>) => <PASSPORT_LOGIN_IDP_STATUS>result.body),
                catchError(this.handleError)
            );
    }

    getPasswordExpirationPolicyData(): Observable<number> {
        const url = `${environment.datahubBaseURI}/api/v2/notifications/properties/policy`;
        return this.http.get(url)
            .pipe(
                map((data: {passwordExpirationPolicy: number}) =>
                    data.passwordExpirationPolicy
                ),
                catchError(this.handleError)
            );
    }

    getRolesOrgTypes(roles: string[]): string[] {
        return roles
            .map((role: string) => this.roleOrgTypesMap.get(role))
            .reduce(
                (result: string[], orgTypes: string[]) => (orgTypes ? [...result, ...orgTypes] : result),
                []
            );
    }

    isPermittedToEditProviderDataSource(user: User,
        isNewApp: boolean,
        application: Application,
        integrationState: IntegrationState,
        institutionGuid: string): boolean {

        const isProvider = this.isProvider(user, false, true);
        const isSysAdmin = this.isSysAdmin(user);
        const isMyApp = isNewApp || (user && application && (application.ownerId === user.orgGuid));
        const isInstitutionalApp = institutionGuid && application && (application.ownerId === institutionGuid);

        return ((isProvider && isMyApp) || (isSysAdmin && isInstitutionalApp));
    }

    isPermittedToEditProviderIntegration(user: User,
        application: Application,
        integrationState: IntegrationState,
        institutionGuid: string,
        providerGuid: string): boolean {

        const isProvider = this.isProvider(user, false, true);
        const isSysAdmin = this.isSysAdmin(user);
        const isNotMyApp = user && application && (application.ownerId !== user.orgGuid);
        const isInstitutionalApp = institutionGuid && application && ((application.ownerId === institutionGuid) || isSysAdmin);
        const isIntegrationStateNotOriginal = (integrationState !== IntegrationState.ORIGINAL)
            && (integrationState !== IntegrationState.DRAFT);

        return (isProvider && isNotMyApp && isInstitutionalApp && isIntegrationStateNotOriginal)
            || (isSysAdmin && isNotMyApp && isInstitutionalApp && isIntegrationStateNotOriginal && (institutionGuid === providerGuid));
    }

    isPermittedToEditVendorIntegration(user: User,
                                       application: Application,
                                       integrationState: IntegrationState,
                                       institutionGuid: string,
                                       providerGuid: string): boolean {

        const isVendor = this.isVendor(user, false, true);
        const isSysAdmin = this.isSysAdmin(user);
        const isIntegrationStateNotOriginal = (integrationState !== IntegrationState.ORIGINAL)
            && (integrationState !== IntegrationState.DRAFT);

        return ((isVendor && !!institutionGuid)
            || (isSysAdmin && !!institutionGuid && isIntegrationStateNotOriginal && (institutionGuid === providerGuid)));
    }

    isPermittedToEditVendorDefaultApplication(user: User,
        institutionGuid: string,
        isDataSchema: boolean): boolean {

        const isVendor = this.isVendor(user, false, true);
        return (isVendor && !institutionGuid) && !isDataSchema;
    }

    isPermittedToEditDataSchema(user: User, integrationState: IntegrationState, isDataSchema: boolean): boolean {
        const isDefaultIntegration = integrationState === IntegrationState.DEFAULT;
        return this.isSysAdmin(user) && (isDefaultIntegration || isDataSchema);
    }

    checkPasswordExpiration(user: User): [boolean, number] {
        const oneDayInMilliseconds = 1000 /*ms*/ * 60 /*sec*/ * 60 /*min*/ * 24 /*hr*/;
        const periodWhenPasswordNeedToBeChangedInMilliseconds = oneDayInMilliseconds * this.getPasswordExpirationPolicy()/*days*/;
        const numberOfDaysToShowNotificationBeforePasswordExpiresInMilliseconds = oneDayInMilliseconds * NUMBER_OF_DAYS_TO_SHOW_NOTIFICATION_BEFORE_PASSWORD_EXPIRES;
        let showPasswordExpirationNotification: boolean;
        let passwordExpireDays: number;

        const passwordExpirationDate = new Date(user.lastPasswordChanged).getTime() + periodWhenPasswordNeedToBeChangedInMilliseconds;
        if ((user.status === UserStatus.ACTIVE)
            && (Date.now() < passwordExpirationDate)) {

            showPasswordExpirationNotification = Date.now() > passwordExpirationDate - numberOfDaysToShowNotificationBeforePasswordExpiresInMilliseconds;
            if (showPasswordExpirationNotification) {
                passwordExpireDays = Math.floor((passwordExpirationDate - Date.now()) / oneDayInMilliseconds);
            }
        }

        return [showPasswordExpirationNotification, passwordExpireDays];
    }

    getMFAConfigFromPassportAuthService(guid: string = null): Observable<UserMfaConfig[]> {
        const url = `${environment.stsServer}/mfa/config`;
        let params = new HttpParams();
        if (guid) {
            params = params.set('guid', guid);
        }
        return this.http.get<MfaConfigs>(url, {params: params})
            .pipe(
                map((res: MfaConfigs) =>
                    res.factorsList
                        ?.sort((a: UserMfaConfig, b: UserMfaConfig) =>
                            MFA_ORDER.indexOf(a.name)  - MFA_ORDER.indexOf(b.name)
                        )
                ),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 404) {
                        return throwError(error);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    sendUserVerificationCodeFromPassportAuthService(phone: string): Observable<any> {
        const url = `${environment.stsServer}/mfa/sms/registration/code`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        const params = new HttpParams()
            .set('phone', phone);
        return this.http.post(url, params.toString(), options);
    }
    sendVerificationCodeAgain(): Observable<boolean> {
        return this.http.post<string>(`${environment.stsServer}/mfa/sms/login/code/resend`, null)
            .pipe(
                map(() => true)
            );
    }

    verifyCodeForUserMFAPhoneChangeFromPassportAuthService(verificationCode: string, phone: string): Observable<string[] | string> {
        const url = `${environment.stsServer}/mfa/sms/registration/code/verify`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        const params = new HttpParams()
            .set('verificationCode', verificationCode)
            .set('phone', phone);
        return this.http.post<string[] | string>(url, params.toString(), options);
    }

    disableUserMFAFromPassportAuthService(guid: string = null): Observable<any> {
        const url = `${environment.stsServer}/mfa/sms/config/disable`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        let params = new HttpParams();
        if (guid) {
            params = params.set('guid', guid);
        }
        return this.http.post(url, params.toString(), options)
            .pipe(catchError(this.handleError));
    }

    regenerateUserMFARecoveryCodesFromPassportAuthService(password: string): Observable<string[]> {
        const url = `${environment.stsServer}/mfa/backup-codes/config/regenerate`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        const params = new HttpParams()
            .set('password', password);
        return this.http
            .post<string[]>(url, params.toString(), options)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 403) {
                        return throwError(error);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    getTotpMfaSecret(): Observable<{secret: string; uri: string}> {
        const url = `${environment.stsServer}/mfa/totp/registration/secret`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        return this.http.get<{secret: string; uri: string}>(url, options)
            .pipe(
                map(({ secret, uri }: {secret: string; uri: string}) => {
                    if (customer.customerShortName !== 'SchoolDay') {
                        uri = uri.replace('totp/SchoolDay:', `totp/${encodeURIComponent(customer.customerShortName)}:`);
                        uri = uri.replace('&issuer=SchoolDay', `&issuer=${encodeURIComponent(customer.customerShortName)}`);
                    }
                    return { secret, uri };
                })
            );
    }

    enableTotpMfaForUser(verificationCode: string): Observable<string[]> {
        const url = `${environment.stsServer}/mfa/totp/registration/code/verify`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        const params = new HttpParams()
            .set('verificationCode', verificationCode);
        return this.http.post<string[]>(url, params.toString(), options);
    }

    disableTotpMfaForUser(guid: string = null): Observable<any> {
        const url = `${environment.stsServer}/mfa/totp/config/disable`;
        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
        };
        let params = new HttpParams();
        if (guid) {
            params = params.set('guid', guid);
        }
        return this.http.post(url, params.toString(), options)
            .pipe(catchError(this.handleError));
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(`Backend returned ${error.status}, ${error.error}`);
        }
        return throwError('Something bad happened, please try again later.');
    }
}

export interface PermissionData {
    permittedRoles: string[];
    permittedOrgTypes: string[];
    result: BehaviorSubject<boolean>;
}

export const DEFAULT_DISABLE_RESEND_CODE_BTN_DURATION = 60;
